import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  TextField,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  changeFacilityAccessCardPin,
  mapFacilityCardPinError,
} from "services/facilityAccessCards/facilityAccessCardService";

const FacilityCardPinSettings = ({ user, visible, close, setSubmitted }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      currentPin: "",
      newPin: "",
      confirmPin: "",
    },
  });
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const newPin = watch("newPin");

  const onSubmit = async (data) => {
    setLoading(true);
    setFormError(null);

    try {
      await changeFacilityAccessCardPin({
        patientUid: user.uid,
        currentPin: data.currentPin,
        newPin: data.newPin,
      });
      reset();
      setSubmitted("PIN changed. Use your new PIN next time.");
      close();
    } catch (error) {
      const mapped = mapFacilityCardPinError(error);
      const message = String(error?.message || "").trim();
      if (mapped) {
        setFormError(mapped);
      } else if (message.includes("Current PIN is incorrect")) {
        setFormError("Current PIN is incorrect.");
      } else {
        setFormError(message || "Unable to change PIN. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setFormError(null);
    close();
  };

  const pinFieldRules = (label) => ({
    required: `${label} is required.`,
    pattern: {
      value: /^\d{6}$/,
      message: "PIN must be exactly 6 digits.",
    },
  });

  return (
    <Dialog open={visible} onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Change PIN</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Enter your current PIN and choose a new 6-digit PIN.
          </DialogContentText>

          <Controller
            name="currentPin"
            control={control}
            rules={pinFieldRules("Current PIN")}
            render={({ field }) => (
              <TextField
                label="Current PIN"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                InputLabelProps={{ shrink: true }}
                fullWidth
                error={!!errors?.currentPin}
                helperText={errors?.currentPin?.message}
                inputProps={{ maxLength: 6, inputMode: "numeric" }}
                sx={{ mb: 2 }}
                {...field}
                onChange={(e) => {
                  field.onChange(
                    String(e.target.value || "")
                      .replace(/\D/g, "")
                      .slice(0, 6),
                  );
                }}
              />
            )}
          />

          <Controller
            name="newPin"
            control={control}
            rules={pinFieldRules("New PIN")}
            render={({ field }) => (
              <TextField
                label="New PIN"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                InputLabelProps={{ shrink: true }}
                fullWidth
                error={!!errors?.newPin}
                helperText={errors?.newPin?.message}
                inputProps={{ maxLength: 6, inputMode: "numeric" }}
                sx={{ mb: 2 }}
                {...field}
                onChange={(e) => {
                  field.onChange(
                    String(e.target.value || "")
                      .replace(/\D/g, "")
                      .slice(0, 6),
                  );
                }}
              />
            )}
          />

          <Controller
            name="confirmPin"
            control={control}
            rules={{
              ...pinFieldRules("Confirm PIN"),
              validate: (value) =>
                value === newPin || "PINs do not match.",
            }}
            render={({ field }) => (
              <TextField
                label="Confirm new PIN"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                InputLabelProps={{ shrink: true }}
                fullWidth
                error={!!errors?.confirmPin}
                helperText={errors?.confirmPin?.message}
                inputProps={{ maxLength: 6, inputMode: "numeric" }}
                sx={{ mb: 2 }}
                {...field}
                onChange={(e) => {
                  field.onChange(
                    String(e.target.value || "")
                      .replace(/\D/g, "")
                      .slice(0, 6),
                  );
                }}
              />
            )}
          />

          {formError && (
            <FormHelperText error sx={{ mt: 1 }}>
              {formError}
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <LoadingButton
            loading={loading}
            type="submit"
            disabled={loading}
            autoFocus
            variant="contained"
          >
            Change PIN
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FacilityCardPinSettings;
